import {
  PRIMITIVE_HEIGHT,
  PRIMITIVE_WIDTH,
  PROCESS_WIDTH,
  STEP_HEIGHT,
  STEP_MIN_WIDTH,
  STEP_PADDING,
} from "../../types/editor.constants";
import { Primitive, Step } from "../../types/TransactionTemplate.types";

const colorCache = new Map<string, string>();

export const getComputedColor = (
  variable: string,
  fallback: string,
): string => {
  if (typeof window === "undefined") return fallback;
  const cached = colorCache.get(variable);
  if (cached) return cached;

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variable)
    .trim();
  const result = value || fallback;
  colorCache.set(variable, result);
  return result;
};

export const clearColorCache = () => colorCache.clear();

export const getPrimWidth = (prim: Primitive): number =>
  prim.type === "process" ? PROCESS_WIDTH : PRIMITIVE_WIDTH;

export const calculateStepHeight = (step: Step): number => {
  if (!step.primitives.length && !step.event) return STEP_HEIGHT;

  let maxY = 0;
  if (step.event) maxY = step.event.y + PRIMITIVE_HEIGHT;
  step.primitives.forEach((p) => {
    maxY = Math.max(maxY, p.y + PRIMITIVE_HEIGHT);
  });

  return Math.max(maxY + STEP_PADDING, STEP_HEIGHT);
};

export const calculateStepWidth = (step: Step): number => {
  if (!step.primitives.length) return STEP_MIN_WIDTH;

  const rightmost = step.primitives.reduce((max, p) => {
    return Math.max(max, p.x + getPrimWidth(p));
  }, 0);

  return Math.max(rightmost + STEP_PADDING, STEP_MIN_WIDTH);
};

export const calculateSchemaDimensions = (
  steps: Step[],
): { width: number; height: number } => {
  if (!steps.length) return { width: 2000, height: 800 };

  let maxWidth = 0;
  let maxHeight = 0;

  for (const step of steps) {
    for (const prim of step.primitives) {
      maxWidth = Math.max(maxWidth, prim.x + getPrimWidth(prim));
      maxHeight = Math.max(maxHeight, step.y + prim.y + PRIMITIVE_HEIGHT);
    }
    if (step.event) {
      maxHeight = Math.max(maxHeight, step.y + step.event.y + PRIMITIVE_HEIGHT);
    }
  }

  return {
    width: Math.max(maxWidth + 200, 2000),
    height: Math.max(maxHeight + 200, 800),
  };
};

export const findPrimitivesInDirection = (
  primitives: Primitive[],
  current: Primitive,
  direction: "left" | "right" | "up" | "down",
  yThreshold = 60,
): { prim: Primitive; idx: number }[] => {
  const indexed = primitives.map((p, idx) => ({ prim: p, idx }));

  switch (direction) {
    case "left":
      return indexed
        .filter(
          ({ prim }) =>
            prim.x < current.x && Math.abs(prim.y - current.y) < yThreshold,
        )
        .sort((a, b) => b.prim.x - a.prim.x);

    case "right":
      return indexed
        .filter(
          ({ prim }) =>
            prim.x > current.x && Math.abs(prim.y - current.y) < yThreshold,
        )
        .sort((a, b) => a.prim.x - b.prim.x);

    case "up":
      return indexed
        .filter(({ prim }) => prim.y < current.y)
        .sort((a, b) => {
          const yDiff =
            Math.abs(a.prim.y - current.y) - Math.abs(b.prim.y - current.y);
          if (Math.abs(yDiff) > 1) return yDiff;
          return (
            Math.abs(a.prim.x - current.x) - Math.abs(b.prim.x - current.x)
          );
        });

    case "down":
      return indexed
        .filter(({ prim }) => prim.y > current.y)
        .sort((a, b) => {
          const yDiff =
            Math.abs(a.prim.y - current.y) - Math.abs(b.prim.y - current.y);
          if (Math.abs(yDiff) > 1) return yDiff;
          return (
            Math.abs(a.prim.x - current.x) - Math.abs(b.prim.x - current.x)
          );
        });
  }
};

export const derivePrimVisuals = (
  data: Record<string, any>,
  currentName: string,
): { name: string; type: string } => {
  let displayName = currentName;

  if (data.type === "function" && data.func) {
    displayName = `${data.link ? data.link + " = " : ""}${data.func}(...)`;
  } else if (data.type === "subtransaction" && data.transactionId) {
    displayName = data.link
      ? `${data.link} = sub(${data.transactionId})`
      : `sub(${data.transactionId})`;
  } else if (data.type === "if") {
    displayName = data.cond ? `if ${data.cond}` : "if";
  } else if (data.type === "for") {
    displayName = `for ${data.id || "i"} in ${data.elems || "items"}`;
  } else {
    displayName = data.func || data.primitive || currentName || "Action";
  }

  return {
    name: displayName,
    type: data.type,
  };
};

export const findMaxXOnRow = (
  primitives: Primitive[],
  y: number,
  threshold = 30,
): number => {
  let maxX = 0;
  for (const p of primitives) {
    if (Math.abs(p.y - y) < threshold) {
      maxX = Math.max(maxX, p.x + getPrimWidth(p));
    }
  }
  return maxX;
};
